import React, { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { adminApi } from "@/lib/admin";
import { absoluteUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AdminOrders() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    adminApi.get("/designs", { params: q ? { q } : {} }).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [q]);

  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <div className="sq-label">Orders</div>
        <h1 className="text-4xl font-heading font-bold mt-1 text-secondary">All designs & quotes</h1>
        <p className="text-sm text-muted-foreground mt-1">{total} total orders across all factories</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search design name, garment, fabric, brand" className="h-12 pl-10" />
      </div>

      <div className="grid gap-4">
        {items.length === 0 && (
          <div className="sq-card p-10 text-center text-muted-foreground">No orders yet.</div>
        )}
        {items.map((d) => (
          <div key={d.id} className="sq-card p-4 flex gap-4">
            <img
              src={absoluteUrl(d.image_url)}
              alt=""
              className="w-24 h-24 rounded-lg object-cover bg-muted shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-bold text-lg truncate">{d.design_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {d.user?.business_name || d.user?.name || "—"} · {d.user?.phone || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-bold text-primary text-lg">
                    {inr(d.quote?.final_price || d.quote?.suggested_price)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {d.quote?.delivery_date || "—"}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <Tag>{d.garment_type}</Tag>
                <Tag>{d.fabric_type}</Tag>
                <Tag>{d.machine_brand}</Tag>
                {d.machine_area && <Tag>{d.machine_area.replace("x", "×")}mm · {d.num_heads}h</Tag>}
                <Tag>{Number(d.stitch_count || 0).toLocaleString("en-IN")} stitches</Tag>
                {d.has_zari && <Tag accent>Zari</Tag>}
                {d.has_sequin && <Tag accent>Sequin</Tag>}
                {d.has_moti && <Tag accent>Moti</Tag>}
              </div>
              {d.bits_detail && Object.keys(d.bits_detail).length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Bits: {Object.keys(d.bits_detail).map((k) => k.replace(/_/g, " ")).join(", ")}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-2">{d.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tag({ children, accent }) {
  return (
    <span className={`px-2 py-0.5 rounded-full border ${accent ? "border-primary/30 bg-primary/10 text-primary" : "border-border bg-muted/40"}`}>
      {children}
    </span>
  );
}
